import { useModal } from "@homebound/beam";
import {
  BaseChangeEventFormInput,
  BaseChangeEventFormState,
} from "src/routes/projects/change-events/guided/baseChangeEventForm";
import { PurchaseOrderCleanupModal } from "src/routes/projects/change-events/guided/workflows/sharedSteps/PurchaseOrderCleanupModal";
import { reconcilableCommitments } from "src/routes/projects/change-events/guided/workflows/sharedSteps/ReconcileImpactedCommitmentsStep";
import { WorkflowFormState } from "src/routes/projects/change-events/guided/workflows/types";
import {
  PurchaseOrderCleanup_ChangeEventFragment,
  usePurchaseOrderCleanupChangeEventLazyQuery,
} from "./PurchaseOrderCleanupModal.generated";

type MaybeOpenCommitmentCleanupModalArgs<F extends BaseChangeEventFormInput> = {
  form: WorkflowFormState<F>;
  onSubmit: () => Promise<void>;
  onSaveDraftAndExit: () => Promise<void>;
};

/**
 * Because a change event can rewrite scope that's already on a purchase order (swapping a product, applying a
 * configuration, adding an option), and the backend won't touch a live PO, we need to block submitting the change
 * event while the user cleans up the commitments by hand.
 */
export function useMaybeOpenCommitmentCleanupModal<F extends BaseChangeEventFormInput>(
  args: MaybeOpenCommitmentCleanupModalArgs<F>,
) {
  const { form, onSubmit, onSaveDraftAndExit } = args;
  const { openModal } = useModal();
  const reviewForm = form as typeof form & BaseChangeEventFormState<F>;
  // no-cache then users can go back to the stepper after taking action and see fresh data
  const [fetchImpactedCommitments] = usePurchaseOrderCleanupChangeEventLazyQuery({ fetchPolicy: "no-cache" });

  return {
    submitWithCommitmentCleanup: async (): Promise<void> => {
      if (!form.canSave()) return; // Sanity check the form can be saved

      const changeEventId = reviewForm.id.value;
      if (changeEventId) {
        const { data } = await fetchImpactedCommitments({ variables: { changeEventId } });
        if (data) {
          const commitments = getImpactedCommitments(data.changeEvent);
          if (commitments.nonEmpty) {
            return openModal({
              size: "lg",
              content: (
                <PurchaseOrderCleanupModal
                  projectId={data.changeEvent.project.id}
                  commitments={commitments}
                  onSaveDraftAndExit={onSaveDraftAndExit}
                />
              ),
            });
          }
        }
      }

      await onSubmit();
    },
  };
}

/**
 * Pull the project items that hold the committed cost (including drafts) the change event touches,
 * then filter impacted commitments that include any of them.
 *
 * That's every project item on the change event's line items, e.g. the scope a configuration or option change adds,
 * updates or removes. For swaps it's the swapped out product's original project item, but plan-based (v2) materials
 * are always $0 -- the plan's money sits on separate cost-holding lines, one per draw -- so the commitment line items
 * need to also match against the original project item's plan-based cost-holding lines.
 */
function getImpactedCommitments(changeEvent: PurchaseOrderCleanup_ChangeEventFragment) {
  const { impactedCommitments = [], lineItems = [], materialVariantSwaps = [] } = changeEvent;
  if (impactedCommitments.isEmpty) return [];

  const projectItemIds = [
    ...lineItems.map((celi) => celi.projectItem),
    ...materialVariantSwaps.flatMap((swap) => [
      swap.originalProjectItem,
      ...swap.originalProjectItem.planBasedCostHoldingProjectItems,
    ]),
  ]
    .filter((pi) => pi.allCommittedCostInCents > 0)
    .map((pi) => pi.id)
    // A swapped product's original project item is usually also one of the change event's line items
    .unique();
  return reconcilableCommitments(impactedCommitments).filter((c) =>
    c.lineItems.some((cli) => projectItemIds.includes(cli.projectItem.id)),
  );
}
